//@flow

import type {
  ChatTrigger,
  TriggerActionRequest,
  TriggerFilterGroup,
  TriggerFiltersFormValues,
  TriggerTextSearchFilter,
  TriggerType,
  CustomerInactivityFilter,
  ActionType,
  TriggerInitProvider,
  ActionMessage,
} from '../../../../entities';
import type {TriggerFields} from '../components/TriggerForm/const';
import type {CreateTriggerRequest, UpdateTriggerRequest} from '../api';
import {
  applyTriggerTypeChange,
  getActionTypeForTriggerType,
  getDefaultExecutionTimeSlot,
  isCustomerInactivityTrigger,
  mergeFiltersForTriggerType,
  DEFAULT_TRIGGER_TYPE,
  FILTER_TYPES,
  MAX_CUSTOMER_INACTIVITY_DAYS,
} from './triggerConfig';
import {
  hourSlotToUtcHhMm,
  resolveExecutionTimeSlot,
} from '../../constants/executionTime';

export function normalizeTriggerName(value: string): string {
  return value.trim().replace(/\s+/g, '_');
}

function getTriggerTypeFromApi(trigger: any): TriggerType {
  return trigger.triggerType || trigger.trigger_type || DEFAULT_TRIGGER_TYPE;
}

function getTriggerIdFromApi(trigger: any): string {
  return trigger.id || trigger.formatted_id || '';
}

function parseFiltersFromApi(filters: any): TriggerFiltersFormValues {
  if (!filters) return {};

  if (Array.isArray(filters)) {
    // Supports both shapes:
    // - New: [{ customer_inactivity: {...}, crm_tags: {...} }]
    // - Legacy: [{ customer_inactivity: {...} }, { crm_tags: {...} }]
    return filters.reduce((acc, group) => {
      if (!group || typeof group !== 'object' || Array.isArray(group)) {
        return acc;
      }
      return {
        ...acc,
        ...group,
      };
    }, {});
  }

  if (typeof filters === 'object') {
    return filters;
  }

  return {};
}

function emptyActionMessage(): ActionMessage {
  return {
    text: '',
    documents: [],
    images: [],
    videos: [],
  };
}

function normalizeMediaMessage(message: any): ActionMessage {
  if (!message || typeof message !== 'object') {
    return emptyActionMessage();
  }

  return {
    text: message.text || '',
    documents: message.documents || [],
    images: message.images || [],
    videos: message.videos || [],
  };
}

function isInitMessageConfigShape(value: any): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  return (
    value.provider != null ||
    value.deviceId != null ||
    value.whatsappWebMessage != null ||
    value.whatsappApiMessage != null
  );
}

function isMediaMessageShape(value: any): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  return (
    typeof value.text === 'string' ||
    Array.isArray(value.images) ||
    Array.isArray(value.videos) ||
    Array.isArray(value.documents)
  );
}

/**
 * Prefer outbound `sendInitMessageConfig`; else API-persisted `actionMessage`
 * shaped as init config (provider, deviceId, whatsapp*Message).
 */
export function resolveInitMessageConfig(action: any): any {
  if (!action) return {};

  if (action.sendInitMessageConfig != null) {
    return action.sendInitMessageConfig;
  }

  if (isInitMessageConfigShape(action.actionMessage)) {
    return action.actionMessage;
  }

  return {};
}

/**
 * Prefer outbound `sendMessageConfig`; else API `actionMessage` when it looks
 * like a media message ({text, images, videos, documents}).
 */
export function resolveSendMessageConfig(action: any): ActionMessage {
  if (!action) return emptyActionMessage();

  if (action.sendMessageConfig != null) {
    return normalizeMediaMessage(action.sendMessageConfig);
  }

  if (
    isMediaMessageShape(action.actionMessage) &&
    !isInitMessageConfigShape(action.actionMessage)
  ) {
    return normalizeMediaMessage(action.actionMessage);
  }

  return emptyActionMessage();
}

export function parseActionContentFromApi(action: any): ActionMessage {
  if (!action) return emptyActionMessage();

  if (getActionTypeFromApi(action) === 'SEND_INIT_MESSAGE') {
    const initConfig = resolveInitMessageConfig(action);
    return normalizeMediaMessage(initConfig.whatsappWebMessage);
  }

  return resolveSendMessageConfig(action);
}

export function getActionTypeFromApi(action: any): ActionType {
  return action?.actionType || 'SEND_MESSAGE';
}

export function normalizeTriggerFromApi(trigger: any): ChatTrigger {
  const triggerType = getTriggerTypeFromApi(trigger);
  let filters = parseFiltersFromApi(trigger.filters);

  if (!Object.keys(filters).length) {
    filters = applyTriggerTypeChange(triggerType);
  }

  return {
    ...trigger,
    id: getTriggerIdFromApi(trigger),
    name: trigger.name,
    executionTime: resolveTopLevelExecutionTime(trigger, filters),
    triggerType,
    enabled: !!trigger.enabled,
    filters: buildTriggerFiltersPayload(filters),
    actions: (trigger.actions || []).map(action => {
      const actionType = getActionTypeFromApi(action);

      if (actionType === 'SEND_INIT_MESSAGE') {
        return {
          actionType,
          sendInitMessageConfig: resolveInitMessageConfig(action),
        };
      }

      return {
        actionType,
        sendMessageConfig: resolveSendMessageConfig(action),
      };
    }),
  };
}

/**
 * Prefer top-level UTC `HH:MM`. Fall back to legacy nested
 * inactivity filter executionTime ({hour, minute} local).
 */
function resolveTopLevelExecutionTime(
  trigger: any,
  filters: TriggerFiltersFormValues
): ?string {
  if (typeof trigger.executionTime === 'string' && trigger.executionTime) {
    return trigger.executionTime.slice(0, 5);
  }

  const legacy = (filters[FILTER_TYPES.CUSTOMER_INACTIVITY]: any)
    ?.executionTime;
  if (legacy && typeof legacy === 'object' && typeof legacy.hour === 'number') {
    const slot = resolveExecutionTimeSlot(legacy);
    return hourSlotToUtcHhMm(slot);
  }

  return undefined;
}

function enrichFiltersForForm(
  filters: TriggerFiltersFormValues,
  inactivityHydrate?: {
    executionTime?: string,
    executionTimeSlot?: string,
  }
): TriggerFiltersFormValues {
  const inactivity = filters[FILTER_TYPES.CUSTOMER_INACTIVITY];

  if (!inactivity) {
    return filters;
  }

  const inactiveDays =
    inactivity.inactiveDays != null
      ? Math.min(
          MAX_CUSTOMER_INACTIVITY_DAYS,
          Math.max(0, inactivity.inactiveDays)
        )
      : MAX_CUSTOMER_INACTIVITY_DAYS;

  // Drop legacy nested executionTime shape; re-attach form-friendly fields below.
  const {
    executionTime: _legacyExecutionTime,
    executionTimeSlot: _legacySlot,
    ...restInactivity
  } = (inactivity: any);

  const nextInactivity: CustomerInactivityFilter = {
    ...restInactivity,
    inactiveDays,
  };

  if (inactivityHydrate?.executionTime) {
    nextInactivity.executionTime = inactivityHydrate.executionTime;
  }
  if (inactivityHydrate?.executionTimeSlot) {
    nextInactivity.executionTimeSlot = inactivityHydrate.executionTimeSlot;
  }

  return {
    ...filters,
    [FILTER_TYPES.CUSTOMER_INACTIVITY]: nextInactivity,
  };
}

export function normalizeTriggerToFormValues(trigger: any): TriggerFields {
  const triggerType = getTriggerTypeFromApi(trigger);
  let filters = parseFiltersFromApi(trigger.filters);

  if (!Object.keys(filters).length) {
    filters = applyTriggerTypeChange(triggerType);
  }

  // Resolve root/legacy execution time BEFORE enrich strips nested legacy fields.
  const rawExecutionTime =
    (typeof trigger.executionTime === 'string' && trigger.executionTime) ||
    resolveTopLevelExecutionTime(trigger, filters);

  const executionTimeSlot = resolveExecutionTimeSlot(rawExecutionTime);
  const executionTimeUtc =
    typeof rawExecutionTime === 'string' && rawExecutionTime
      ? rawExecutionTime.slice(0, 5)
      : isCustomerInactivityTrigger(triggerType)
      ? hourSlotToUtcHhMm(executionTimeSlot)
      : undefined;

  filters = enrichFiltersForForm(
    filters,
    isCustomerInactivityTrigger(triggerType)
      ? {
          executionTime: executionTimeUtc,
          executionTimeSlot,
        }
      : undefined
  );
  filters = mergeFiltersForTriggerType(triggerType, filters);

  const action = (trigger.actions || [])[0] || {};
  const actionType = getActionTypeForTriggerType(triggerType);
  const initConfig = resolveInitMessageConfig(action);
  const provider: ?TriggerInitProvider = initConfig.provider;
  const mediaMessage = parseActionContentFromApi({
    ...action,
    actionType,
  });
  const apiMessage = initConfig.whatsappApiMessage || {};

  return {
    id: trigger.name || '',
    triggerType,
    filters,
    executionTimeSlot: isCustomerInactivityTrigger(triggerType)
      ? executionTimeSlot
      : getDefaultExecutionTimeSlot(),
    response: mediaMessage.text,
    attachments: [],
    savedDocuments: mediaMessage.documents || [],
    savedImages: mediaMessage.images || [],
    savedVideos: mediaMessage.videos || [],
    actionType,
    provider: provider || undefined,
    deviceId: initConfig.deviceId || '',
    templateName: apiMessage.template || '',
    templateLang: apiMessage.templateLang || '',
    templateComponents: apiMessage.components || [],
  };
}

function sanitizeTextSearchFilter(textSearch: any): any {
  const sanitized: any = {
    ...textSearch,
    searchText: (textSearch.searchText || '').trim(),
  };

  if (!sanitized.enabledRelatedTexts) {
    delete sanitized.relatedSearchTexts;
    return sanitized;
  }

  const relatedSearchTexts = sanitizeSimilarWords(
    sanitized.relatedSearchTexts || []
  );

  if (relatedSearchTexts.length > 0) {
    sanitized.relatedSearchTexts = relatedSearchTexts;
  } else {
    delete sanitized.relatedSearchTexts;
  }

  return sanitized;
}

export function sanitizeSimilarWords(words: string[]): string[] {
  const seen = new Set();

  return words
    .map(word => (typeof word === 'string' ? word.trim() : ''))
    .filter(word => {
      if (!word) return false;
      const key = word.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

export function buildTriggerFiltersPayload(
  filters: TriggerFiltersFormValues
): TriggerFilterGroup[] {
  const merged: any = {};

  Object.keys(filters || {})
    .filter(filterType => filters[filterType] != null)
    .forEach(filterType => {
      const filterValue = filters[filterType];

      if (filterType === FILTER_TYPES.TRIGGER_TEXT_SEARCH && filterValue) {
        merged[filterType] = sanitizeTextSearchFilter(filterValue);
        return;
      }

      if (filterType === FILTER_TYPES.CUSTOMER_INACTIVITY && filterValue) {
        const inactivity: any = {...filterValue};
        const rawInactiveDays =
          inactivity.inactiveDays != null
            ? inactivity.inactiveDays
            : MAX_CUSTOMER_INACTIVITY_DAYS;

        merged[filterType] = {
          inactiveDays: Math.min(
            MAX_CUSTOMER_INACTIVITY_DAYS,
            Math.max(0, rawInactiveDays)
          ),
        };
        return;
      }

      merged[filterType] = filterValue;
    });

  if (Object.keys(merged).length === 0) {
    return [];
  }

  return [(merged: TriggerFilterGroup)];
}

function buildMediaMessageFromForm(formValues: TriggerFields): ActionMessage {
  return {
    text: formValues.response || '',
    documents: formValues.savedDocuments || [],
    images: formValues.savedImages || [],
    videos: formValues.savedVideos || [],
  };
}

export function buildTriggerActionFromForm(
  formValues: TriggerFields
): TriggerActionRequest {
  const actionType = getActionTypeForTriggerType(
    formValues.triggerType || DEFAULT_TRIGGER_TYPE
  );

  if (actionType === 'SEND_INIT_MESSAGE') {
    const provider = formValues.provider || 'WHATSAPP_WEB';
    const sendInitMessageConfig: any = {
      provider,
      deviceId: formValues.deviceId || '',
    };

    if (provider === 'WHATSAPP_API') {
      sendInitMessageConfig.whatsappApiMessage = {
        template: formValues.templateName || '',
        templateLang: formValues.templateLang || '',
        components: formValues.templateComponents || [],
      };
    } else {
      sendInitMessageConfig.whatsappWebMessage = buildMediaMessageFromForm(
        formValues
      );
    }

    return {
      actionType: 'SEND_INIT_MESSAGE',
      sendInitMessageConfig,
    };
  }

  return {
    actionType: 'SEND_MESSAGE',
    sendMessageConfig: buildMediaMessageFromForm(formValues),
  };
}

export function buildTriggerActionsPayload(
  actions: TriggerActionRequest[]
): TriggerActionRequest[] {
  return actions.map(action => {
    if (action.actionType === 'SEND_INIT_MESSAGE') {
      return {
        actionType: 'SEND_INIT_MESSAGE',
        sendInitMessageConfig: action.sendInitMessageConfig,
      };
    }

    return {
      actionType: 'SEND_MESSAGE',
      sendMessageConfig: action.sendMessageConfig || {
        text: '',
        documents: [],
        images: [],
        videos: [],
      },
    };
  });
}

function buildExecutionTimeFromForm(formValues: TriggerFields): ?string {
  if (!isCustomerInactivityTrigger(formValues.triggerType)) {
    return undefined;
  }

  const slot = formValues.executionTimeSlot || getDefaultExecutionTimeSlot();

  return hourSlotToUtcHhMm(slot);
}

export function buildCreateTriggerPayload(
  formValues: TriggerFields
): CreateTriggerRequest {
  const payload: CreateTriggerRequest = {
    name: normalizeTriggerName(formValues.id),
    triggerType: formValues.triggerType,
    filters: buildTriggerFiltersPayload(formValues.filters),
    actions: buildTriggerActionsPayload([
      buildTriggerActionFromForm(formValues),
    ]),
  };

  const executionTime = buildExecutionTimeFromForm(formValues);
  if (executionTime) {
    payload.executionTime = executionTime;
  }

  return payload;
}

export function buildUpdateTriggerPayload(
  formValues: TriggerFields,
  enabled?: boolean
): UpdateTriggerRequest {
  const payload: UpdateTriggerRequest = {
    name: normalizeTriggerName(formValues.id),
    filters: buildTriggerFiltersPayload(formValues.filters),
    actions: buildTriggerActionsPayload([
      buildTriggerActionFromForm(formValues),
    ]),
  };

  const executionTime = buildExecutionTimeFromForm(formValues);
  if (executionTime) {
    payload.executionTime = executionTime;
  }

  if (typeof enabled === 'boolean') {
    payload.enabled = enabled;
  }

  return payload;
}

export function getTextSearchFromTrigger(
  trigger: ChatTrigger
): ?TriggerTextSearchFilter {
  const filters = parseFiltersFromApi(trigger.filters);
  return filters[FILTER_TYPES.TRIGGER_TEXT_SEARCH] || null;
}

export function getInactivityFromTrigger(
  trigger: ChatTrigger
): ?CustomerInactivityFilter {
  const filters = parseFiltersFromApi(trigger.filters);
  return filters[FILTER_TYPES.CUSTOMER_INACTIVITY] || null;
}

export function getDefaultFormValues(): TriggerFields {
  const triggerType = DEFAULT_TRIGGER_TYPE;

  return {
    id: '',
    triggerType,
    filters: applyTriggerTypeChange(triggerType),
    executionTimeSlot: getDefaultExecutionTimeSlot(),
    response: '',
    attachments: [],
    savedDocuments: [],
    savedImages: [],
    savedVideos: [],
    actionType: getActionTypeForTriggerType(triggerType),
    provider: undefined,
    deviceId: '',
    templateName: '',
    templateLang: '',
    templateComponents: [],
  };
}
